import React from "react";
import { Card } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import { NoRecordCard } from "../../../common/components";
import { Table } from "../../../common/components/core";
import { useSelector } from "../../../redux/hooks";
import {
    currentRolesSelector,
    currentUserEmailSelector,
    currentUserAccountNameSelector
} from "../../../redux/slices/auth/selectors";
import { BesuClaimListItem } from "../../../types/claim";
import { BesuProjectState } from "../../../types/project";
import {
    claimListStatusToClaimStatus,
    hasClaimListAction,
    hasClaimListClaimAction,
    isClaimVisibleForCurrentUser,
} from "../../../utils/besuClaimList";
import { isNodeDeveloper } from "../../../utils/role";
import { formatToDate, shortenUUID } from "../../../utils/common";

import { PureClaimStatusBadge } from "./ClaimStatusBadge";

type ClaimListProps = {
    project: BesuProjectState;
    claims: BesuClaimListItem[];
};
const ClaimList = ({ project, claims }: ClaimListProps): JSX.Element => {
    const history = useHistory();
    const currentUserEmail = useSelector(currentUserEmailSelector);
    const currentRoles = useSelector(currentRolesSelector);
    const currentUserAccountName = useSelector(currentUserAccountNameSelector);

    const sortedClaims = [...claims].filter((claim) => {
        return isClaimVisibleForCurrentUser(claim, currentUserAccountName, project.mainContractors);
    }).sort((a, b) => {
        const aHasAction = hasClaimListAction(currentRoles, a, currentUserEmail);
        const bHasAction = hasClaimListAction(currentRoles, b, currentUserEmail);
        if (aHasAction !== bHasAction) {
            return aHasAction ? -1 : 1;
        }
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    return sortedClaims.length === 0 ? (
        <NoRecordCard />
    ) : (
        <Card className="mt-3 border-0">
            <Card.Body className="px-0 py-0">
                <table className={'custom-table'}>
                    <thead>
                        <tr>
                            <Table.HeaderCell width={40} className="text-center">
                                CTA
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160}>
                                Claim ID
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160}>
                                Creation Date
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                width={160}
                                className="text-center"
                            >
                                No. of Invoice Submissions
                            </Table.HeaderCell>
                            {isNodeDeveloper() && (
                                <Table.HeaderCell width={160}>
                                    Service Provider
                                </Table.HeaderCell>
                            )
                            }
                            <Table.HeaderCell width={160} className="text-end">
                                Status
                            </Table.HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedClaims.map((claim) => {
                            const hasAction = hasClaimListAction(currentRoles, claim, currentUserEmail);
                            const claimStatus = claimListStatusToClaimStatus(claim.claimStatus);
                            const hasClaimLevelAction = hasClaimListClaimAction(currentRoles, claim, currentUserEmail);
                            return (
                                <Table.BodyRow
                                    key={claim.referenceId}
                                    hasAction={hasAction}
                                    onClick={() =>
                                        history.push(
                                            `/project/${project.referenceId}/claim/${claim.referenceId}`,
                                        )
                                    }
                                    onKeyUp={(event: KeyboardEvent) => {
                                        (event.keyCode === 13 || event.keyCode === 32) &&
                                            history.push(
                                                `/project/${project.referenceId}/claim/${claim.referenceId}`,
                                            )
                                        }
                                }
                                >
                                    <td className="cta-cell">
                                        {hasAction && <span className="cta" title="action required" aria-label="action required" />}
                                    </td>
                                    <td>
                                        {shortenUUID(claim.referenceId)}
                                    </td>
                                    <td>
                                        {formatToDate(claim.createdAt)}
                                    </td>
                                    <td className="text-center">
                                        {claim.invoiceCount}
                                    </td>
                                    {isNodeDeveloper() && (
                                        <td>
                                            {claim.contractorDisplayName || claim.contractorAccountName}
                                        </td>
                                    )}
                                    <td className="text-end">
                                        <PureClaimStatusBadge
                                            status={claimStatus}
                                            hasClaimAction={hasClaimLevelAction}
                                            role={currentRoles}
                                        />
                                    </td>
                                </Table.BodyRow>
                            );
                        })}
                    </tbody>
                </table>
            </Card.Body>
        </Card>
    );

};

export default ClaimList;
